import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Edit, Trash2, Plus, Globe, Loader2 } from "lucide-react"
import { useBlogCategories } from '@/hooks/use-blog-categories'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { BlogCategory, blogCategoryService } from '@/services/other-services'
import { toast } from 'sonner'
import { RefreshCw } from "lucide-react"

const SUPPORTED_LANGUAGES = [
    { code: 'tr', name: 'Türkçe' },
    { code: 'en', name: 'English' },
    { code: 'fr', name: 'Français' },
    { code: 'es', name: 'Español' },
    { code: 'ru', name: 'Русский' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'ar', name: 'العربية' },
]

const localizedStringSchema = z.object({
    tr: z.string().min(2, { message: 'En az 2 karakter olmalıdır' }),
    en: z.string().optional().or(z.literal('')),
    fr: z.string().optional().or(z.literal('')),
    es: z.string().optional().or(z.literal('')),
    ru: z.string().optional().or(z.literal('')),
    nl: z.string().optional().or(z.literal('')),
    ar: z.string().optional().or(z.literal('')),
})

const categorySchema = z.object({
    name: localizedStringSchema,
    slug: localizedStringSchema,
    description: localizedStringSchema.optional(),
    isActive: z.boolean().default(true),
    orderIndex: z.coerce.number().default(0),
    seo: z.object({
        meta_title: localizedStringSchema.optional(),
        meta_description: localizedStringSchema.optional(),
        meta_keywords: localizedStringSchema.optional(),
    }).optional(),
})

type CategoryFormValues = z.infer<typeof categorySchema>

export function CategoryList() {
    const { data: categories, isLoading, createCategory, updateCategory, deleteCategory } = useBlogCategories()
    const [isDialogOpen, setIsDialogOpen] = useState(false)
    const [isTranslating, setIsTranslating] = useState(false)
    const [editingCategory, setEditingCategory] = useState<BlogCategory | null>(null)

    const form = useForm<CategoryFormValues>({
        resolver: zodResolver(categorySchema),
        defaultValues: {
            name: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            slug: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            isActive: true,
            orderIndex: 0,
            seo: {
                meta_title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                meta_description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                meta_keywords: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
            }
        }
    })

    const generateSlug = (text: string) => {
        return text
            .trim()
            .replace(/İ/g, 'i')
            .replace(/I/g, 'i')
            .replace(/ı/g, 'i')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/ğ/g, 'g')
            .replace(/ü/g, 'u')
            .replace(/ş/g, 's')
            .replace(/ö/g, 'o')
            .replace(/ç/g, 'c')
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');
    }

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value
        form.setValue('name.tr', value)
        if (!editingCategory) {
            form.setValue('slug.tr', generateSlug(value))
        }
    }

    const handleOpenDialog = (category?: BlogCategory) => {
        if (category) {
            setEditingCategory(category)
            form.reset({
                name: category.name,
                slug: category.slug,
                description: category.description || { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                isActive: category.isActive ?? true,
                orderIndex: category.orderIndex ?? 0,
                seo: category.seo || {
                    meta_title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_keywords: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                }
            })
        } else {
            setEditingCategory(null)
            form.reset({
                name: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                slug: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                isActive: true,
                orderIndex: 0,
                seo: {
                    meta_title: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_description: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                    meta_keywords: { tr: '', en: '', fr: '', es: '', ru: '', nl: '', ar: '' },
                }
            })
        }
        setIsDialogOpen(true)
    }

    const onSubmit = async (values: CategoryFormValues) => {
        if (editingCategory) {
            await updateCategory({ id: editingCategory.id || editingCategory._id!, data: values })
        } else {
            await createCategory(values)
        }
        setIsDialogOpen(false)
    }

    const handleBulkTranslate = async () => {
        if (!confirm('Tüm kategoriler için eksik çeviriler AI ile tamamlanacak. Emin misiniz?')) return

        setIsTranslating(true)
        try {
            const res = await blogCategoryService.bulkTranslate()
            toast.success(res.message || 'Toplu çeviri başarıyla tamamlandı')
            window.location.reload() // Refresh to see changes
        } catch (error) {
            console.error('Bulk translate error:', error)
            toast.error('Toplu çeviri sırasında bir hata oluştu')
        } finally {
            setIsTranslating(false)
        }
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Kategoriler</h2>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleBulkTranslate}
                        disabled={isTranslating}
                        className="border-primary/20 hover:bg-primary hover:text-primary-foreground transition-all duration-300"
                    >
                        {isTranslating ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                            <RefreshCw className="mr-2 h-4 w-4" />
                        )}
                        Kategorileri Çevir
                    </Button>
                    <Button onClick={() => handleOpenDialog()}>
                        <Plus className="mr-2 h-4 w-4" /> Yeni Kategori
                    </Button>
                </div>
            </div>

            <Card>
                <CardContent className="pt-6">
                    {isLoading ? (
                        <div className="flex h-32 items-center justify-center">Yükleniyor...</div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>İsim (TR)</TableHead>
                                    <TableHead>Slug (TR)</TableHead>
                                    <TableHead>Sıra</TableHead>
                                    <TableHead>Durum</TableHead>
                                    <TableHead className="text-right">İşlemler</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {categories?.map((category) => (
                                    <TableRow key={category.id || category._id} className="group cursor-pointer">
                                        <TableCell className="font-medium group-hover:text-primary transition-colors">
                                            {typeof category.name === 'object' ? category.name.tr : category.name}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {typeof category.slug === 'object' ? category.slug.tr : category.slug}
                                        </TableCell>
                                        <TableCell>{category.orderIndex}</TableCell>
                                        <TableCell>
                                            <Badge variant={category.isActive ? 'default' : 'secondary'}>
                                                {category.isActive ? 'Aktif' : 'Pasif'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(category)}>
                                                    <Edit className="h-4 w-4" />
                                                </Button>
                                                <Button variant="ghost" size="icon" onClick={() => deleteCategory(category.id || category._id!)} className="text-destructive">
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>{editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}</DialogTitle>
                    </DialogHeader>

                    <Form {...form}>
                        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                            <div className="relative w-full rounded-lg border px-4 py-3 text-sm flex items-start gap-3 bg-blue-50/50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300">
                                <Globe className="h-4 w-4 mt-0.5" />
                                <div>
                                    <p className="font-medium mb-1 flex items-center gap-1">✨ Yapay Zeka Çevirisi Aktif</p>
                                    Türkçe kategori adı ve açıklaması baz alınarak tüm diller otomatik olarak çevrilecektir. Maddelerin çokluğuna ve içeriğin uzunluğuna göre bu işlem biraz vakit alabilir, lütfen işlem tamamlanana kadar bekleyiniz.
                                </div>
                            </div>

                            <Tabs defaultValue="tr" className="w-full">
                                <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 h-auto p-1 bg-muted/50">
                                    {SUPPORTED_LANGUAGES.map((lang) => (
                                        <TabsTrigger key={lang.code} value={lang.code} className="py-2 text-xs">
                                            {lang.name}
                                        </TabsTrigger>
                                    ))}
                                </TabsList>

                                {SUPPORTED_LANGUAGES.map((lang) => (
                                    <TabsContent key={lang.code} value={lang.code} className="space-y-4 pt-4">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <FormField
                                                control={form.control}
                                                name={`name.${lang.code}` as any}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Kategori Adı ({lang.name})</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={(field.value as string) || ''}
                                                                onChange={lang.code === 'tr' ? handleNameChange : field.onChange}
                                                                placeholder={`${lang.name} isim`}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name={`slug.${lang.code}` as any}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Slug ({lang.name})</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                value={(field.value as string) || ''}
                                                                placeholder="kategori-slug"
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                        <FormField
                                            control={form.control}
                                            name={`description.${lang.code}` as any}
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Açıklama ({lang.name})</FormLabel>
                                                    <FormControl>
                                                        <Textarea
                                                            {...field}
                                                            value={(field.value as string) || ''}
                                                            placeholder={`${lang.name} açıklama`}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />

                                        <div className="pt-4 mt-4 border-t border-dashed">
                                            <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                                                <Globe className="w-4 h-4 text-primary" /> SEO Ayarları ({lang.name})
                                            </h4>
                                            <div className="space-y-4">
                                                <FormField
                                                    control={form.control}
                                                    name={`seo.meta_title.${lang.code}` as any}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Meta Başlık</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="Boş bırakılırsa kategori adı kullanılır" {...field} value={field.value || ''} />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`seo.meta_description.${lang.code}` as any}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Meta Açıklama</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="Arama sonuçlarında görünecek açıklama" {...field} value={field.value || ''} />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name={`seo.meta_keywords.${lang.code}` as any}
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>Meta Anahtar Kelimeler</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="Virgülle ayırarak yazın" {...field} value={field.value || ''} />
                                                            </FormControl>
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </div>
                                    </TabsContent>
                                ))}
                            </Tabs>

                            <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                                <FormField
                                    control={form.control}
                                    name="orderIndex"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Sıralama</FormLabel>
                                            <FormControl>
                                                <Input type="number" {...field} />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                <FormField
                                    control={form.control}
                                    name="isActive"
                                    render={({ field }) => (
                                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                                            <div className="space-y-0.5">
                                                <FormLabel>Aktif</FormLabel>
                                            </div>
                                            <FormControl>
                                                <Input
                                                    type="checkbox"
                                                    className="w-4 h-4"
                                                    checked={field.value}
                                                    onChange={field.onChange}
                                                // Note: Shadcn Switch is better, but consistency with current file
                                                />
                                            </FormControl>
                                        </FormItem>
                                    )}
                                />
                            </div>

                            <DialogFooter>
                                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
                                <Button type="submit" disabled={form.formState.isSubmitting}>
                                    {form.formState.isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Yapay zeka çevirileri yapılıyor, lütfen bekleyin...
                                        </>
                                    ) : editingCategory ? (
                                        'Güncelle'
                                    ) : (
                                        'Oluştur'
                                    )}
                                </Button>
                            </DialogFooter>
                        </form>
                    </Form>
                </DialogContent>
            </Dialog>
        </div>
    )
}
